import React, { useState } from "react";
import { useTheme } from "../../context/ThemeContext";
import TimeZone from "../../components/CalendarShareLinkToolComponents/TimeZone";
import HeaderSection from "../../components/HeaderSection/HeaderSection";
import BottomSection from "../../components/BottomSection/BottomSection";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";

const EventForm = () => {
    const [eventData, setEventData] = useState({
        title: "",
        description: "",
        location: "",
        startDate: "",
        endDate: "",
        timezone: "UTC +05:00 Asia/Karachi",
        customLink: "",
        isOnline: false,
        isAllDay: false,
    });
    const { darkMode } = useTheme();

    const [submittedEvents, setSubmittedEvents] = useState([]);
    const today = new Date().toISOString().split("T")[0];

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEventData({
            ...eventData,
            [name]: type === "checkbox" ? checked : value,
        });
    };

    const generateCalendarLinks = (event) => {
        const { title, startDate, endDate, location, description } = event;
        const start = new Date(startDate).toISOString().replace(/[-:.]/g, "");
        const end = new Date(endDate).toISOString().replace(/[-:.]/g, "");

        const googleLink = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
            title
        )}&dates=${start}/${end}&location=${encodeURIComponent(
            location
        )}&details=${encodeURIComponent(description)}&sf=true&output=xml`;
        const yahooLink = `https://calendar.yahoo.com/?v=60&view=d&type=20&title=${encodeURIComponent(
            title
        )}&st=${start}&et=${end}&desc=${encodeURIComponent(
            description
        )}&in_loc=${encodeURIComponent(location)}`;
        const outlookLink = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(
            title
        )}&startdt=${start}&enddt=${end}&location=${encodeURIComponent(
            location
        )}&body=${encodeURIComponent(description)}`;
        const office365Link = `https://outlook.office.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(
            title
        )}&startdt=${start}&enddt=${end}&location=${encodeURIComponent(
            location
        )}&body=${encodeURIComponent(description)}`;
        const appleLink = `https://calendar.apple.com/?event=${encodeURIComponent(
            title
        )}&start=${start}&end=${end}&location=${encodeURIComponent(
            location
        )}&notes=${encodeURIComponent(description)}`;

        return {
            google: googleLink,
            yahoo: yahooLink,
            outlook: outlookLink,
            office365: office365Link,
            apple: appleLink,
        };
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        console.log(eventData);

        const calendarLinks = generateCalendarLinks(eventData);
        setSubmittedEvents([
            ...submittedEvents,
            { ...eventData, calendarLinks },
        ]);

        setEventData({
            title: "",
            description: "",
            location: "",
            startDate: "",
            endDate: "",
            timezone: "UTC +05:00 Asia/Karachi",
            customLink: "",
            isOnline: false,
            isAllDay: false,
        });
    };

    return (
        <>
            <HeaderSection toolId="calendarShareLink" />
            <main className="max-w-4xl min-h-screen mx-auto p-4 lg:max-w-full lg:px-4">
                <article className="bg-white rounded-xl dark:bg-gray-800 shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
                        <h1 className="text-2xl font-bold mb-2 flex items-center">
                            <FontAwesomeIcon
                                icon={faEnvelope}
                                className="mr-3"
                            />
                            Calendar Share Link Generator
                        </h1>
                        <p className="opacity-90">
                            Generate calendar share links in real-time with
                            comprehensive validation checks
                        </p>
                    </div>
                    <form
                        onSubmit={handleSubmit}
                        className="bg-white dark:bg-gray-800 p-6 rounded-lg"
                    >
                        <header>
                            <h1 className="text-2xl font-semibold text-gray-800 dark:text-white mb-6">
                                Event Information
                            </h1>
                        </header>

                        <fieldset className="mb-4">
                            <label
                                htmlFor="title"
                                className="block text-gray-700 dark:text-gray-200 mb-2"
                            >
                                Event Title
                            </label>
                            <input
                                type="text"
                                id="title"
                                name="title"
                                value={eventData.title}
                                onChange={handleChange}
                                placeholder="Event Title"
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-green-300 transition-all text-gray-700 dark:text-white dark:bg-gray-700"
                                required
                            />
                        </fieldset>

                        <fieldset className="mb-4">
                            <label
                                htmlFor="description"
                                className="block text-gray-700 dark:text-gray-200 mb-2"
                            >
                                Description
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                value={eventData.description}
                                onChange={handleChange}
                                placeholder="Event Description"
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-green-300 transition-all text-gray-700 dark:text-white dark:bg-gray-700 h-24"
                            ></textarea>
                        </fieldset>

                        <fieldset className="mb-4">
                            <div className="flex justify-between items-center mb-2">
                                <label
                                    htmlFor={
                                        eventData.isOnline
                                            ? "meeting"
                                            : "location"
                                    }
                                    className="block text-gray-700 dark:text-gray-200"
                                >
                                    {eventData.isOnline
                                        ? "Meeting Link"
                                        : "Event Location"}
                                </label>
                                <div className="flex items-center">
                                    <label
                                        htmlFor="online-event"
                                        className="text-gray-700 dark:text-gray-200 mr-2"
                                    >
                                        Online Event?
                                    </label>
                                    <input
                                        id="online-event"
                                        type="checkbox"
                                        name="isOnline"
                                        checked={eventData.isOnline}
                                        onChange={handleChange}
                                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700"
                                    />
                                </div>
                            </div>
                            <input
                                id={eventData.isOnline ? "meeting" : "location"}
                                type="text"
                                name="location"
                                value={eventData.location}
                                onChange={handleChange}
                                placeholder={
                                    eventData.isOnline
                                        ? "Meeting Link"
                                        : "Event Location"
                                }
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-green-300 transition-all text-gray-700 dark:text-white dark:bg-gray-700"
                                required
                            />
                        </fieldset>

                        <fieldset className="mb-4">
                            <div className="flex justify-between items-center mb-2">
                                <label
                                    htmlFor="startDate"
                                    className="block text-gray-700 dark:text-gray-200"
                                >
                                    Event Start Date
                                </label>
                                <div className="flex items-center">
                                    <label
                                        htmlFor="all-day-event"
                                        className="text-gray-700 dark:text-gray-200 mr-2"
                                    >
                                        All Day Event?
                                    </label>
                                    <input
                                        id="all-day-event"
                                        type="checkbox"
                                        name="isAllDay"
                                        checked={eventData.isAllDay}
                                        onChange={handleChange}
                                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700"
                                    />
                                </div>
                            </div>

                            {eventData.isAllDay ? (
                                <input
                                    type="date"
                                    id="startDate"
                                    name="startDate"
                                    value={eventData.startDate}
                                    onChange={handleChange}
                                    min={today}
                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-green-300 transition-all text-gray-700 dark:text-white dark:bg-gray-700"
                                    required
                                />
                            ) : (
                                <input
                                    type="datetime-local"
                                    id="startDate"
                                    name="startDate"
                                    value={eventData.startDate}
                                    onChange={handleChange}
                                    min={today + "T00:00"}
                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-green-300 transition-all text-gray-700 dark:text-white dark:bg-gray-700"
                                    required
                                />
                            )}
                        </fieldset>

                        <fieldset className="mb-6">
                            <label
                                htmlFor="endDate"
                                className="block text-gray-700 dark:text-gray-200 mb-2"
                            >
                                Event End Date
                            </label>
                            {eventData.isAllDay ? (
                                <input
                                    type="date"
                                    id="endDate"
                                    name="endDate"
                                    value={eventData.endDate}
                                    onChange={handleChange}
                                    min={today}
                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-green-300 transition-all text-gray-700 dark:text-white dark:bg-gray-700"
                                    required
                                />
                            ) : (
                                <input
                                    type="datetime-local"
                                    id="endDate"
                                    name="endDate"
                                    value={eventData.endDate}
                                    onChange={handleChange}
                                    min={today + "T00:00"}
                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-green-300 transition-all text-gray-700 dark:text-white dark:bg-gray-700"
                                    required
                                />
                            )}
                        </fieldset>

                        {!eventData.isAllDay && <TimeZone />}

                        <button
                            type="submit"
                            className="w-full mt-6 bg-green-500 hover:bg-green-600 text-white font-medium py-3 px-4 rounded-lg transition-all duration-300 shadow-md hover:shadow-lg"
                            aria-label="Create event link"
                        >
                            Create Event Link
                        </button>
                    </form>

                    {submittedEvents.map((event, index) => (
                        <section
                            key={index}
                            className="mt-8 bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm dark:shadow-gray-900/20"
                        >
                            <header className="space-y-2 mb-4">
                                <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
                                    Direct Calendar Links
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300">
                                    Add this event directly to your preferred
                                    calendar
                                </p>
                            </header>

                            <nav>
                                <ul className="space-y-3">
                                    <li className="flex items-center">
                                        <a
                                            href={event.calendarLinks.google}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 hover:underline transition-colors"
                                            aria-label="Add to Google Calendar"
                                        >
                                            <svg
                                                className="w-5 h-5 mr-2"
                                                fill="currentColor"
                                                viewBox="0 0 24 24"
                                                aria-hidden="true"
                                            >
                                                <path d="M12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zm1 19v-2h-2v2h2zm2.564-7.05l-.984.984c-.564.564-1.03.984-1.03 1.5v.5H12v-.5c0-1.07.47-1.64 1.03-2.2l1.06-1.06c.41-.41.66-.98.66-1.59 0-1.31-1.06-2.38-2.38-2.38-1.3 0-2.38 1.07-2.38 2.38H7.62c0-2.39 1.94-4.34 4.34-4.34 2.39 0 4.34 1.95 4.34 4.34 0 .95-.38 1.81-1 2.44z" />
                                            </svg>
                                            Google Calendar
                                        </a>
                                    </li>

                                    <li className="flex items-center">
                                        <a
                                            href={event.calendarLinks.yahoo}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 hover:underline transition-colors"
                                            aria-label="Add to Yahoo Calendar"
                                        >
                                            <svg
                                                className="w-5 h-5 mr-2"
                                                fill="currentColor"
                                                viewBox="0 0 24 24"
                                                aria-hidden="true"
                                            >
                                                <path d="M18.54 9.2h-5.01l-1.52 4.91-1.54-4.91H5.46l4.13 11.6h1.82l4.13-11.6zM12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0zm0 22.5c-5.8 0-10.5-4.7-10.5-10.5S6.2 1.5 12 1.5 22.5 6.2 22.5 12 17.8 22.5 12 22.5z" />
                                            </svg>
                                            Yahoo Calendar
                                        </a>
                                    </li>

                                    <li className="flex items-center">
                                        <a
                                            href={event.calendarLinks.outlook}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 hover:underline transition-colors"
                                            aria-label="Add to Outlook Calendar"
                                        >
                                            <svg
                                                className="w-5 h-5 mr-2"
                                                fill="currentColor"
                                                viewBox="0 0 24 24"
                                                aria-hidden="true"
                                            >
                                                <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 22.5c-5.799 0-10.5-4.7-10.5-10.5S6.201 1.5 12 1.5 22.5 6.201 22.5 12 17.799 22.5 12 22.5zm-1.125-6.75h2.25v-9h-2.25v9z" />
                                            </svg>
                                            Outlook Calendar
                                        </a>
                                    </li>

                                    <li className="flex items-center">
                                        <a
                                            href={event.calendarLinks.office365}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 hover:underline transition-colors"
                                            aria-label="Add to Office 365 Calendar"
                                        >
                                            <svg
                                                className="w-5 h-5 mr-2"
                                                fill="currentColor"
                                                viewBox="0 0 24 24"
                                                aria-hidden="true"
                                            >
                                                <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 22.5c-5.799 0-10.5-4.7-10.5-10.5S6.201 1.5 12 1.5 22.5 6.201 22.5 12 17.799 22.5 12 22.5zm5.625-6.75h-11.25v-9h11.25v9z" />
                                            </svg>
                                            Office 365 Calendar
                                        </a>
                                    </li>

                                    <li className="flex items-center">
                                        <a
                                            href={event.calendarLinks.apple}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 hover:underline transition-colors"
                                            aria-label="Add to Apple Calendar"
                                        >
                                            <svg
                                                className="w-5 h-5 mr-2"
                                                fill="currentColor"
                                                viewBox="0 0 24 24"
                                                aria-hidden="true"
                                            >
                                                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                                            </svg>
                                            Apple Calendar
                                        </a>
                                    </li>
                                </ul>
                            </nav>
                        </section>
                    ))}
                </article>
            </main>
            <BottomSection toolId="calendarShareLink" />
        </>
    );
};

export default EventForm;
